/**
 * The CrazyGames SDK, and nothing else.
 *
 * This file exists only in the CrazyGames bundle: every export below is guarded
 * by `ON_CRAZYGAMES`, which is a folded literal, so in the web and Steam builds
 * the bundler deletes the lot and no script from sdk.crazygames.com is ever
 * referenced, let alone fetched.
 *
 * What we use it for
 * ------------------
 *   - **the player's name.** A signed-in visitor should not have to type a name
 *     they already have, and the portal requires their username to be visible in
 *     game so friends can recognise each other. Prefilled, never forced: the
 *     field stays editable, and somebody who is not signed in types a name
 *     exactly as they do on kaelmoor.io.
 *   - **invite links.** The game already has private rooms with a five-character
 *     code and a `?room=` deep link, which is precisely the shape the portal's
 *     "play with friends" flow wants. We hand it the room code and it produces
 *     the link and the button.
 *
 * What we deliberately do not use it for
 * --------------------------------------
 *   - **ads.** No monetisation until there is a legal entity to be paid into.
 *     The SDK's ad module is never touched and the Google layer is compiled out,
 *     so this build carries no advertising of any kind.
 *
 * On being defensive
 * ------------------
 * Every call below is feature-detected and wrapped. A portal SDK is somebody
 * else's code on somebody else's CDN, loaded into a frame we do not control: it
 * can fail to load, be blocked, or rename a method between versions. None of
 * those may take the game with them — the worst outcome permitted here is "no
 * invite button", never a menu that does not appear. **If a call silently stops
 * working, check it against docs.crazygames.com/sdk before assuming the game
 * broke it.**
 */

import { ON_CRAZYGAMES, apiUrl } from './platform';

const SDK_SRC = 'https://sdk.crazygames.com/crazygames-sdk-v3.js';

/** How long to wait for somebody else's CDN before starting the menu anyway. */
const SDK_TIMEOUT_MS = 3000;

let sdk: any = null;
let ready = false;

/** What `init` found, for the verifier and for a glance at a live page. */
let status: 'off' | 'loading' | 'ready' | 'failed' | 'timeout' = ON_CRAZYGAMES ? 'loading' : 'off';

function load(): Promise<void> {
  return new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = SDK_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { status = 'failed'; resolve(); };
    document.head.appendChild(s);
  });
}

/**
 * Load and initialise the SDK. Resolves either way — never rejects, never hangs.
 *
 * The timeout is the point of the function. Awaiting a third-party script before
 * the menu appears is only acceptable if the wait is bounded: a player on a bad
 * connection, or with the CDN blocked, still gets a playable menu three seconds
 * later with a name field they can type into.
 */
export async function initPlatform(): Promise<void> {
  if (!ON_CRAZYGAMES) return;

  await Promise.race([
    load(),
    new Promise<void>(r => setTimeout(() => { if (status === 'loading') status = 'timeout'; r(); }, SDK_TIMEOUT_MS)),
  ]);

  sdk = (window as any).CrazyGames?.SDK ?? null;
  if (!sdk) { if (status === 'loading') status = 'failed'; return; }

  try {
    await sdk.init?.();
    ready = true;
    status = 'ready';
  } catch {
    status = 'failed';
  }
}

/**
 * The signed-in player's portal username, or `''`.
 *
 * Truncated to the same 16 characters the name field accepts, because a longer
 * username silently cut off by `maxlength` would look like the game mangled it.
 * The server's own name rules still apply on top — a username the moderation
 * filter rejects comes back as a corrected one, which is the same path a typed
 * name takes.
 */
export async function portalUsername(): Promise<string> {
  if (!ready || !sdk) return '';
  try {
    const user = await sdk.user?.getUser?.();
    const name = (user?.username ?? '').trim();
    return name.slice(0, 16);
  } catch {
    return '';
  }
}

/**
 * Tell the portal which room this player is in, so friends can be sent to it.
 *
 * Called on entering a lobby and again when it is no longer joinable. Passing an
 * empty code takes the button down, which is what leaving a room means.
 */
export function setRoom(code: string) {
  if (!ready || !sdk) return;
  try {
    if (code) sdk.game?.showInviteButton?.({ roomId: code });
    else sdk.game?.hideInviteButton?.();
  } catch { /* an SDK that cannot do this is not a reason to stop playing */ }
}

/**
 * The room code somebody was invited to, if they arrived through an invite link.
 *
 * Read once at boot, before the menu is wired up, and handled exactly like the
 * `?room=CODE` deep link the game already supports — the two are the same
 * gesture arriving by different routes.
 */
export function invitedRoom(): string {
  if (!ready || !sdk) return '';
  try {
    return (sdk.game?.getInviteParam?.('roomId') ?? '').toString().trim().toUpperCase();
  } catch {
    return '';
  }
}

/** Bracket actual play, so the portal knows when not to interrupt. */
export function gameplayStart() {
  if (!ready || !sdk) return;
  try { sdk.game?.gameplayStart?.(); } catch { /* advisory only */ }
}

export function gameplayStop() {
  if (!ready || !sdk) return;
  try { sdk.game?.gameplayStop?.(); } catch { /* advisory only */ }
}

/**
 * Tell the server somebody opened the menu.
 *
 * On kaelmoor.io this is counted where the page is served, which cannot work
 * here: the portal serves the page and we never hear about it. One request at
 * boot, no body, no identifier — the same nothing that the page count records,
 * so the funnel's first step means the same thing on both sides.
 *
 * Failure is silence. A visitor missing from a chart is not worth an error.
 */
export function countArrival() {
  if (!ON_CRAZYGAMES) return;
  fetch(apiUrl('/arrive?src=crazygames'), { method: 'GET', cache: 'no-store' })
    .catch(() => { /* a counter is never worth interrupting anybody for */ });
}

/** Test hook: what this layer thinks it is doing. Mirrors `adState()`. */
export function platformState() {
  return { platform: __PLATFORM__, sdk: status, ready };
}
